import { OPERATION_TIMEZONE, addDays, dayInOperationTimezone, formatDay } from '@/utils/onDemandRequestRules';

/**
 * Piso semanal da data do pedido sob demanda (coleta e movimentação de equipamento). Quem decide é
 * o musa-agreements (`weekly-date-floor`), na criação — aqui só se evita oferecer uma data que
 * voltaria recusada. As duas pontas precisam dizer a mesma coisa.
 *
 * Não fecha o pedido: limita para quando ele pode ser. Até o corte de sexta-feira ainda dá para
 * pedir para a segunda seguinte; depois do corte, ou no sábado e no domingo, a primeira data é a
 * terça-feira. De segunda a quinta não há piso. O piso soma-se à antecedência do contrato.
 *
 * O corte é onde a restrição **começa**: às 15:59 a segunda ainda vale, às 16:00 não. Dia e hora são
 * os de Brasília, não os do navegador: `getDay()` e `getHours()` leriam o fuso da máquina, e quem
 * estivesse em outro fuso veria o piso aparecer ou sumir na hora errada.
 */

export const DEFAULT_WEEKLY_CUTOFF = '16:00';

/** A Athie tem um corte mais tarde que os demais. */
export const ATHIE_WEEKLY_CUTOFF = '17:00';

/** Estabelecimentos da Athie se identificam pelo prefixo `AW |` no nome. */
const ATHIE_NAME_PATTERN = /^\s*AW\s*\|/i;

export function isAthieEstablishment(name: string | null | undefined): boolean {
  return ATHIE_NAME_PATTERN.test(name ?? '');
}

export function weeklyCutoffFor(name: string | null | undefined): string {
  return isAthieEstablishment(name) ? ATHIE_WEEKLY_CUTOFF : DEFAULT_WEEKLY_CUTOFF;
}

/** Dia da semana (`Mon`…`Sun`) e `HH:MM` de Brasília. */
function operationClock(now: Date): { weekday: string; clock: string } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: OPERATION_TIMEZONE,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(now);
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? '';

  return { weekday: part('weekday'), clock: `${part('hour')}:${part('minute')}` };
}

/** Primeiro dia (`YYYY-MM-DD`) que o piso permite, ou `null` quando não há piso agora. */
export function weeklyFirstAllowedDay(name: string | null | undefined, now: Date = new Date()): string | null {
  const { weekday, clock } = operationClock(now);

  // "HH:MM" compara como texto; >= porque o corte já é restrição
  const afterCutoff = weekday === 'Fri' && clock >= weeklyCutoffFor(name);

  // dias até a terça: da sexta são 4, do sábado 3, do domingo 2
  const daysToTuesday = afterCutoff ? 4 : weekday === 'Sat' ? 3 : weekday === 'Sun' ? 2 : null;

  return daysToTuesday === null ? null : addDays(dayInOperationTimezone(now), daysToTuesday);
}

/** O piso soma-se ao que a antecedência já permite: vale a data mais tardia. */
export function applyWeeklyFloor(minDate: string, floor: string | null): string {
  return floor && floor > minDate ? floor : minDate;
}

/** Por que a data mais cedo é a terça, com o corte do estabelecimento. */
export function describeWeeklyFloor(name: string | null | undefined, floor: string): string {
  return `Pedidos feitos na sexta-feira depois das ${weeklyCutoffFor(name)}, no sábado ou no domingo `
    + `só podem ser para terça-feira em diante (a partir de ${formatDay(floor)}).`;
}
